/**
 * Utility functions
 */

import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format currency
 */
export function formatCurrency(
  amount: number,
  currency: string = "USD",
  locale: string = "en-US"
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(amount);
}

/**
 * Format an extraction line-item amount for display.
 *
 * Line-item money fields (`unit_price`, `amount`) are typed as non-null
 * `number` in InvoiceData.LineItem, but they originate from unvalidated LLM
 * extraction JSON (canonical_json), which the API forwards without runtime
 * normalization — so a null/undefined value can reach render despite the type.
 * A bare `v.toLocaleString(...)` on that null threw and blanked the whole
 * document page (prod incident). The parameter is `unknown` on purpose: the
 * runtime value is whatever the LLM emitted, not the `number` the type claims,
 * so anything that is not a finite number (null, undefined, a string, an
 * object such as `{toLocaleString: null}`, NaN, Infinity) returns the
 * missing-value placeholder rather than rendering `$NaN` / `[object Object]`
 * or throwing. A finite number formats as currency; an invalid currency code
 * falls back to a plain grouped number with no symbol, so a non-USD invoice is
 * never silently mislabeled as dollars. Never throws inside render.
 */
export function formatLineAmount(
  v: unknown,
  currency: string = "USD",
  missing: string = "—"
): string {
  if (typeof v !== "number" || !Number.isFinite(v)) return missing;
  try {
    return v.toLocaleString("en-US", { style: "currency", currency });
  } catch {
    // Invalid currency code (RangeError). v is a known finite number here, so
    // this second call cannot throw. Drop the symbol rather than assume "$".
    return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse a date string for display.
 *
 * Date-only strings ("2026-09-10" — how extraction stores invoice_date /
 * due_date, and how Postgres returns a `date` column) carry no timezone, so
 * `new Date(str)` reads them as UTC midnight and every negative-offset
 * timezone renders the previous day. Build those from local components
 * instead. Strings that do carry time/zone information are left to the
 * platform parser.
 *
 * Returns null when the value can't be parsed.
 */
export function parseCalendarDate(date: string | Date): Date | null {
  if (date instanceof Date) return isNaN(date.getTime()) ? null : date;
  // Extraction fields are typed `string` but originate from unvalidated LLM
  // JSON, so a number can reach here. Return null rather than throwing —
  // this runs inside render, where a TypeError blanks the whole page.
  if (typeof date !== "string") return null;
  const dateOnly = DATE_ONLY_RE.exec(date.trim());
  if (!dateOnly) {
    const d = new Date(date);
    return isNaN(d.getTime()) ? null : d;
  }
  const [year, month, day] = [dateOnly[1], dateOnly[2], dateOnly[3]].map(Number);
  const d = new Date(year, month - 1, day);
  // The component constructor rolls over out-of-range parts ("2026-13-01"
  // becomes Jan 2027), which would render a date the document never stated.
  // Round-trip the parts so a misread extraction falls back to its raw text.
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return null;
  }
  return d;
}

/**
 * Format date
 */
export function formatDate(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions
): string {
  const d = parseCalendarDate(date);
  if (!d) return typeof date === "string" ? date : "";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  });
}

interface FormatRelativeTimeOptions {
  now?: string | Date | number;
  maxDays?: number;
  justNowLabel?: string;
}

/**
 * Format relative time
 */
export function formatRelativeTime(date: string | Date, options: FormatRelativeTimeOptions = {}): string {
  const d = parseCalendarDate(date);
  if (!d) return typeof date === "string" ? date : "";
  const now = options.now instanceof Date
    ? options.now
    : typeof options.now === "number"
      ? new Date(options.now)
      : typeof options.now === "string"
        ? new Date(options.now)
        : new Date();
  const maxDays = options.maxDays ?? 7;
  const justNowLabel = options.justNowLabel ?? "Just now";
  const diff = now.getTime() - d.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > maxDays) {
    return formatDate(d);
  } else if (days > 0) {
    return `${days}d ago`;
  } else if (hours > 0) {
    return `${hours}h ago`;
  } else if (minutes > 0) {
    return `${minutes}m ago`;
  } else {
    return justNowLabel;
  }
}

/**
 * Truncate text
 */
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + "...";
}

/**
 * Sleep utility
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Format a display label that includes both name and ID/number.
 * Returns "Name (ID)" when both exist, or whichever is available.
 */
export function formatNameWithId(
  name: string | null | undefined,
  id: string | number | null | undefined,
  fallback: string = "Unknown"
): string {
  const hasName = name != null && String(name).trim() !== "";
  const hasId = id != null && String(id).trim() !== "";
  if (hasName && hasId && String(name).trim() !== String(id).trim()) {
    return `${name} (${id})`;
  }
  if (hasName) return String(name);
  if (hasId) return String(id);
  return fallback;
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: Parameters<T>) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}
